import type { Dictionary, Translation } from '@/i18n/types';

const ro = {
  accountAria: 'Cont: {{name}}',
  brandAria: 'AKIS — pagina principală',
  closeMenuAria: 'Închide meniul',
  footerAddress: 'Bd. Ștefan cel Mare și Sfânt 162, Chișinău',
  footerPhone: 'Tel: 022 204 550',
  langSwitchAria: 'Schimbă limba',
  mainNavAria: 'Navigare principală',
  openMenuAria: 'Deschide meniul',
  skipLink: 'Sari la conținut',
  appTitle: 'AKIS — Sistemul de cunoștințe și inovare în agricultură',
  titleSuffix: 'AKIS',
  commonSearchPlaceholder: 'Caută în platformă…',
  euStripDefault:
    'Sistemul informațional AKIS este dezvoltat în cadrul politicii agricole naționale, în conformitate cu HG nr. 630/2025, cu respectarea standardelor de identitate și vizibilitate pentru finanțările europene.',
  footerAgencyAgeAlt: 'Agenția de Guvernare Electronică',
  footerAgencyGovAlt: 'Guvernul Republicii Moldova',
  footerBottomCookies: 'Politica de cookies',
  footerBottomPrivacy: 'Politica de confidențialitate',
  footerBottomTerms: 'Politica de utilizare a site-ului',
  footerColAssistance: 'Asistență',
  footerColParticipants: 'Participanți',
  footerColPlatform: 'Platformă',
  footerColResources: 'Resurse',
  footerCopyright: '© 2026 Ministerul Agriculturii și Industriei Alimentare · Găzduit pe MCloud',
  footerDescription:
    'Instrumentul oficial prin care Ministerul Agriculturii și Industriei Alimentare facilitează schimbul de informații între fermieri, consilieri, cercetători și instituții.',
  footerLinkAdvisors: 'Bază de date consilieri',
  footerLinkAdvisoryServices: 'Servicii de consiliere',
  footerLinkAssociations: 'Asociații profesionale',
  footerLinkCommunity: 'Comunitate și forumuri',
  footerLinkContactForm: 'Formular de contact',
  footerLinkEvents: 'Calendar evenimente',
  footerLinkFunding: 'Subvenții și granturi',
  footerLinkLibrary: 'Bibliotecă digitală',
  footerLinkNews: 'Știri și articole',
  footerLinkPresentation: 'Prezentare proiect',
  footerLinkResearch: 'Rezultate de cercetare',
  footerLinkResearchOrgs: 'Organizații de cercetare',
  footerLinkSupportAgencies: 'Agenții de sprijin',
  footerLinkTrainingInstitutions: 'Instituții de formare',
  footerLinkTrainings: 'Calendar instruiri',
  footerMudAttribution:
    'Bazat pe Modelul Unitar de Design dezvoltat de Agenția de Guvernare Electronică a Republicii Moldova · licențiat sub CC BY-NC-ND 4.0.',
  footerTagline: 'Sistem de cunoștințe și inovare în agricultură',
  govbarAccessibility: 'Accesibilitate',
  govbarMinistry: 'Ministerul Agriculturii și Industriei Alimentare',
  govbarOfficial: 'Un serviciu oficial al Guvernului Republicii Moldova',
  langIndicatorFallback:
    'Această pagină nu este disponibilă în limba engleză; se afișează versiunea în limba română.',
  langIndicatorPartial:
    'O parte din conținutul acestei pagini este disponibilă doar în limba română.',
  navAdvisorsShort: 'Consilieri',
  navCatalog: 'Catalog',
  navEventsShort: 'Evenimente',
  navAccessibilityStatement: 'Declarație de accesibilitate',
  navHome: 'Acasă',
  navInformation: 'Informații',
  navLogout: 'Ieșire',
  navMap: 'Harta AKIS',
  navMpass: 'Autentifică-te prin M-Pass',
  navMpassShort: 'Autentificare',
  navAdminLogin: 'Autentificare administrator',
  navAdminLoginShort: 'Administrator',
  navNewsShort: 'Noutăți',
  navSearch: 'Căutare',
  navSearchButton: 'Caută',
  navSitemap: 'Hartă Site',
  navTrainingsShort: 'Instruiri',
} as const;

const en = {
  accountAria: 'Account: {{name}}',
  brandAria: 'AKIS — home page',
  closeMenuAria: 'Close menu',
  footerAddress: '162 Ștefan cel Mare și Sfânt Blvd, Chișinău',
  footerPhone: 'Tel: +373 22 204 550',
  langSwitchAria: 'Switch language',
  mainNavAria: 'Main navigation',
  openMenuAria: 'Open menu',
  skipLink: 'Skip to content',
  appTitle: 'AKIS — Agricultural Knowledge and Innovation System',
  titleSuffix: 'AKIS',
  commonSearchPlaceholder: 'Search the platform…',
  euStripDefault:
    'The AKIS information system is developed within the national agricultural policy, in accordance with Government Decision 630/2025, in compliance with the identity and visibility standards for European funding.',
  footerAgencyAgeAlt: 'e-Governance Agency',
  footerAgencyGovAlt: 'Government of the Republic of Moldova',
  footerBottomCookies: 'Cookie policy',
  footerBottomPrivacy: 'Privacy policy',
  footerBottomTerms: 'Website terms of use',
  footerColAssistance: 'Assistance',
  footerColParticipants: 'Participants',
  footerColPlatform: 'Platform',
  footerColResources: 'Resources',
  footerCopyright: '© 2026 Ministry of Agriculture and Food Industry · Hosted on MCloud',
  footerDescription:
    'The official instrument through which the Ministry of Agriculture and Food Industry facilitates the exchange of information between farmers, advisers, researchers and institutions.',
  footerLinkAdvisors: 'Advisers database',
  footerLinkAdvisoryServices: 'Advisory services',
  footerLinkAssociations: 'Professional associations',
  footerLinkCommunity: 'Community and forums',
  footerLinkContactForm: 'Contact form',
  footerLinkEvents: 'Events calendar',
  footerLinkFunding: 'Subsidies and grants',
  footerLinkLibrary: 'Digital library',
  footerLinkNews: 'News and articles',
  footerLinkPresentation: 'Project overview',
  footerLinkResearch: 'Research results',
  footerLinkResearchOrgs: 'Research organisations',
  footerLinkSupportAgencies: 'Support agencies',
  footerLinkTrainingInstitutions: 'Training institutions',
  footerLinkTrainings: 'Training calendar',
  footerMudAttribution:
    'Based on the Unified Design Model developed by the e-Governance Agency of the Republic of Moldova · licensed under CC BY-NC-ND 4.0.',
  footerTagline: 'Agricultural Knowledge and Innovation System',
  govbarAccessibility: 'Accessibility',
  govbarMinistry: 'Ministry of Agriculture and Food Industry',
  govbarOfficial: 'An official service of the Government of the Republic of Moldova',
  langIndicatorFallback:
    'This page is not yet available in English; the Romanian version is shown.',
  langIndicatorPartial: 'Some of the content on this page is available only in Romanian.',
  navAdvisorsShort: 'Advisers',
  navCatalog: 'Catalogue',
  navEventsShort: 'Events',
  navAccessibilityStatement: 'Accessibility statement',
  navHome: 'Home',
  navInformation: 'Information',
  navLogout: 'Sign out',
  navMap: 'AKIS map',
  navMpass: 'Sign in with M-Pass',
  navMpassShort: 'M-Pass',
  navAdminLogin: 'Administrator sign-in',
  navAdminLoginShort: 'Administrator',
  navNewsShort: 'News',
  navSearch: 'Search',
  navSearchButton: 'Search',
  navSitemap: 'Sitemap',
  navTrainingsShort: 'Trainings',
} satisfies Translation<keyof typeof ro>;

export const publicLayout = { ro, en } satisfies Dictionary;

export type PublicLayoutKey = keyof typeof ro;
